import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import IntegrationFAQs from "@/components/IntegrationFAQs";
import OnboardingVideo from "@/components/OnboardingVideo";
import { HeaderTextBlock } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useGetCheckKlaviyoOptIn,
  usePostKlaviyoOptIn,
  usePostUpdateOnboardingProgress,
} from "@/requests/hooks/collect";
import { NAVIGATE_BACKWARDS, NAVIGATE_FORWARDS } from "@/utils/const";
import formatError from "@/utils/formatError";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import useIsMobile from "@/utils/useIsMobile";
import { faCheckSquare, faSpinner } from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import klaviyoOptInUpdateMp4 from "@/assets/videos/collect/onboarding/klaviyo-opt-in-update.mp4";

const KlaviyoOptIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data } = usePostKlaviyoOptIn();
  const {
    data: checkOptInData,
    error,
    refetch,
  } = useGetCheckKlaviyoOptIn(!!data);
  const {
    mutate: updateOnboardingProgress,
    isSuccess,
    isPending,
  } = usePostUpdateOnboardingProgress();

  useEffect(() => {
    if (isSuccess)
      navigate(getOnboardingUrl("KLAVIYO_OPTIN_UPDATED"), {
        state: {
          direction: NAVIGATE_FORWARDS,
        },
      });
  }, [isSuccess]);

  const onClickGoBack = () => {
    navigate(getOnboardingUrl("SHOPIFY_ENABLED"), {
      state: {
        direction: NAVIGATE_BACKWARDS,
      },
    });
  };

  const onClickContinue = () => {
    updateOnboardingProgress({
      screen: "KLAVIYO_OPTIN_UPDATED",
      flow: "MARKETING",
    });
  };

  const onClickRefetch = () => {
    refetch();
  };

  const renderFAQs = () => <IntegrationFAQs />;

  const renderDescriptionText = () => (
    <ol className="flex list-decimal flex-col gap-10 ps-20 text-start text-primary-dark">
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoOptIn.left.header.description.step1">
          first
          <Button
            as="a"
            href={`https://www.klaviyo.com/list/${data?.singleOptInListId}/settings`}
            trackingId={{
              eventName: "onboarding.collect.klaviyo.access-settings-url",
            }}
            variant="link"
            width="unset"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="w-15 h-15"
            testId="klaviyo-settings-link">
            one
          </Button>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoOptIn.left.header.description.step2">
          one
          <span className="font-bold">two</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoOptIn.left.header.description.step3">
          one
          <span className="font-bold">two</span>
          three
          <span className="font-bold">four</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoOptIn.left.header.description.step4">
          one
          <span className="font-bold">two</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoOptIn.left.header.description.step5">
          one
          <span className="font-bold">two</span>
        </Trans>
      </li>
    </ol>
  );

  const renderLoadingAlertTitle = () => (
    <div className="flex flex-row items-center gap-20 text-primary-dark">
      <FontAwesomeIcon
        icon={faSpinner}
        className="h-25 w-25 text-primary"
        spin
      />
      {t("emailOnboarding.klaviyoOptIn.right.optInIsNotEnabled")}
    </div>
  );

  const renderApiKeyErrorAlertTitle = () => (
    <div className="">
      <Trans i18nKey={"emailOnboarding.klaviyoOptIn.right.apiKeyError"}>
        zero
        <Button
          trackingId={{ eventName: "onboarding.collect.navigate-back" }}
          onClick={onClickGoBack}
          variant="link"
          width="unset">
          one
        </Button>
        two
      </Trans>
    </div>
  );

  const renderErrorAlertTitle = () => (
    <div className="w-ful flex flex-row flex-wrap items-center justify-start gap-10 md:flex-nowrap md:justify-between md:gap-0">
      {t("generic.error")}

      <Button as="button" onClick={onClickRefetch} variant="link" width="unset">
        {t("emailOnboarding.klaviyoOptIn.right.errorButton")}
      </Button>
    </div>
  );

  const renderHeaderTextBlock = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.klaviyoOptIn.left.header.feature")}
      featureTextColor="primary"
      headingText={t("emailOnboarding.klaviyoOptIn.left.header.heading")}
      headingTextColor="secondary"
      customHtml={renderDescriptionText()}
      customHtmlPosition="bottom"
    />
  );

  const renderAlert = () => {
    const { errors: coreApiErrors } = formatError(error);

    if (coreApiErrors) {
      if (coreApiErrors?.[0]?.code === "INVALID_KLAVIYO_API_KEY")
        return (
          <Alert
            iconClassName="w-25 h-25"
            variant="error"
            titleSlot={renderApiKeyErrorAlertTitle()}
          />
        );

      return (
        <Alert
          iconClassName="w-25 h-25"
          variant="error"
          titleSlot={renderErrorAlertTitle()}
        />
      );
    } else if (checkOptInData?.singleOptInListChecked) {
      return (
        <Alert
          iconClassName="w-25 h-25"
          icon={faCheckSquare}
          variant="success"
          titleSlot={t("emailOnboarding.klaviyoOptIn.right.optInIsEnabled")}
        />
      );
    } else {
      return (
        <Alert
          variant="info"
          iconClassName="hidden"
          titleSlot={renderLoadingAlertTitle()}
        />
      );
    }
  };

  const renderCTA = () => (
    <div className="mt-30 flex w-full flex-row items-center justify-end">
      <div className="w-full md:w-[unset]">
        <Button
          variant="primary"
          onClick={onClickContinue}
          disabled={!checkOptInData?.singleOptInListChecked}
          trackingId={{
            eventName: "onboarding.collect.klaviyo.complete-setup",
          }}
          loading={isPending}>
          {t("emailOnboarding.klaviyoOptIn.right.continue")}
        </Button>
      </div>
    </div>
  );

  const renderOnboardingVideo = () => (
    <OnboardingVideo videoLocation={klaviyoOptInUpdateMp4} />
  );

  const renderLeftPanel = () => (
    <>
      {renderHeaderTextBlock()}
      {!isMobile && renderFAQs()}
    </>
  );
  const renderRightPanel = () => (
    <>
      {renderOnboardingVideo()}
      <div className="mt-30">{renderAlert()}</div>
      {renderCTA()}
      {!!isMobile && renderFAQs()}
    </>
  );

  return (
    <SplitLayout
      leftPanel={renderLeftPanel()}
      rightPanel={renderRightPanel()}
      backButtonPath={getOnboardingUrl("SHOPIFY_ENABLED")}
    />
  );
};

export default KlaviyoOptIn;
